/**
 * Runtime configuration helper
 * Reads from window.runtimeConfig (set by widget.jsx or Helm chart) with fallback to import.meta.env (for dev mode)
 * Приоритет: mountParams (B2BDemo.mount) > URL параметры > iframe data-атрибуты > window.runtimeConfig > import.meta.env > defaults
 *
 * ВАЖНО: config использует getter'ы для динамического чтения window.runtimeConfig,
 * так как он устанавливается в widget.jsx ПОСЛЕ того как все модули импортированы.
 *
 * mountParams имеет НАИВЫСШИЙ приоритет - параметры переданные через B2BDemo.mount()
 * не могут быть переопределены URL параметрами или iframe атрибутами.
 */
import { getConfigFromUrl, getConfigFromIframeDataAttributes, getUrlParam } from '../utils/urlParams';
import { composeDealerPageUrl } from './dealerUrl';

/**
 * Hardcoded website pages для andys-motors.com
 * Используется как fallback когда websitePages не настроены в API
 * Карта сайта соответствует b2b_agent/lc_graphs/prompts/andys_knowledge/sitemap/
 */
const ANDYS_MOTORS_WEBSITE_PAGES = [
  { id: 'home', label: 'Home', path: '/', description: 'Main page with new arrivals and company overview' },
  { id: 'used-cars', label: 'Used Cars', path: '/used-cars/', description: 'Full inventory of used cars with filters' },
  { id: 'car-rental', label: 'Car Rental', path: '/car-rental/', description: 'Online car rental booking system' },
  { id: 'leasing', label: 'Leasing', path: '/leasing/', description: 'Monthly leasing options from €250+VAT' },
  { id: 'british-forces', label: 'British Forces', path: '/british-forces/', description: 'Dedicated services for British Forces personnel' },
  { id: 'special-offers', label: 'Special Offers', path: '/special-offers/', description: 'Discounted and reduced price vehicles' },
  { id: 'about-us', label: 'About Us', path: '/about-us/', description: 'Company history and services overview' },
  { id: 'contact-us', label: 'Contact Us', path: '/contact-us/', description: 'Contact form and office information' },
  { id: 'careers', label: 'Careers', path: '/careers/', description: 'Current job openings' },
];

/**
 * Получить mount параметры из window.b2bMountParams
 * Имеют НАИВЫСШИЙ приоритет - устанавливаются в widget.jsx ДО импорта React компонентов
 */
const getMountParams = () => {
  if (typeof window !== 'undefined' && window.b2bMountParams) {
    return window.b2bMountParams;
  }
  return {};
};

/**
 * Получить текущее значение runtime config
 * Вызывается динамически каждый раз для поддержки widget mode
 */
const getRuntimeConfig = () => {
  if (typeof window !== 'undefined' && window.runtimeConfig) {
    return window.runtimeConfig;
  }
  return {};
};

/**
 * Получить URL параметры (кешируются при первом вызове)
 */
let cachedUrlParams = null;
const getUrlParams = () => {
  if (cachedUrlParams === null) {
    cachedUrlParams = getConfigFromUrl();
  }
  return cachedUrlParams;
};

/**
 * Получить iframe data-attributes (кешируются при первом вызове)
 */
let cachedIframeParams = null;
const getIframeParams = () => {
  if (cachedIframeParams === null) {
    cachedIframeParams = getConfigFromIframeDataAttributes();
  }
  return cachedIframeParams;
};

/**
 * Логотип standalone-витрины (demo.shiftgears.ai), когда дилерский не передан
 * явно (#1441). Вариант для СВЕТЛОГО фона, с тёмными буквами: его видно и на
 * белой бумаге при печати, и на светлой плашке лендинга. `-dark` — наоборот,
 * белые буквы для тёмного фона (герой главной), на печати он невидим.
 */
export const STANDALONE_LOGO_URL = '/assets/logo-horizontal-light.svg';
/** Прежний запасной логотип (#844) — остаётся дилерским лендингам по `?token`. */
export const LEGACY_DEALER_LOGO_URL = '/assets/landing-logo-badge.png';

/**
 * Config объект с динамическими getter'ами
 * Читает window.runtimeConfig каждый раз при обращении к свойству
 */
export const config = {
  // API URLs (b2b-api по умолчанию)
  get CAR_API_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // mountParams.apiUrl имеет НАИВЫСШИЙ приоритет
    return mount.apiUrl || runtime.VITE_CAR_API_URL || import.meta.env.VITE_CAR_API_URL || '';
  },
  get API_BASE_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // mountParams.apiUrl имеет НАИВЫСШИЙ приоритет
    return mount.apiUrl || runtime.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || '';
  },
  get BROWSER_CONTROL_WS_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // Явно заданный WS URL имеет приоритет.
    const explicit =
      runtime.VITE_BROWSER_CONTROL_WS_URL ||
      import.meta.env.VITE_BROWSER_CONTROL_WS_URL;
    if (explicit) return explicit;
    // Fallback: деривируем из apiUrl — browser-control WS живёт на том же
    // b2b-api backend, эндпоинт `wss://<host>/api/browser-control`. Нужно для
    // auto-init пути (bootstrap.jsx populateLegacyConfig не выставляет
    // VITE_BROWSER_CONTROL_WS_URL, в отличие от widget.jsx setRuntimeConfig) —
    // без деривы getter возвращал '' → new WebSocket('') резолвился на текущую
    // host-страницу вместо b2b-api.
    const apiUrl =
      mount.apiUrl ||
      runtime.VITE_API_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL;
    if (apiUrl) {
      return (
        apiUrl.replace(/\/api\/?$/, '').replace(/^http/, 'ws') +
        '/api/browser-control'
      );
    }
    return '';
  },

  // Frontend Token для авторизации в b2b-api Browser Control
  get FRONTEND_TOKEN() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const urlParams = getUrlParams();
    const iframeParams = getIframeParams();
    // mountParams имеет НАИВЫСШИЙ приоритет (из B2BDemo.mount())
    return mount.frontendToken || urlParams.frontendToken || iframeParams.frontendToken || runtime.VITE_FRONTEND_TOKEN || import.meta.env.VITE_FRONTEND_TOKEN || '';
  },

  // Organization и Inventory для b2b-api
  get ORGANIZATION_ID() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const urlParams = getUrlParams();
    const iframeParams = getIframeParams();
    // mountParams имеет НАИВЫСШИЙ приоритет (из B2BDemo.mount())
    const result = mount.organizationId || urlParams.organizationId || iframeParams.organizationId || runtime.VITE_ORGANIZATION_ID || import.meta.env.VITE_ORGANIZATION_ID || '1';
    // Use console.warn for production debugging (console.log is stripped by terser)
    console.warn('[RuntimeConfig] ORGANIZATION_ID:', result, {
      mountParams: mount.organizationId,
      urlParams: urlParams.organizationId,
      iframeParams: iframeParams.organizationId,
      runtime: runtime.VITE_ORGANIZATION_ID,
      envFallback: import.meta.env.VITE_ORGANIZATION_ID
    });
    return result;
  },
  get INVENTORY_SLUG() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const urlParams = getUrlParams();
    const iframeParams = getIframeParams();
    // mountParams имеет НАИВЫСШИЙ приоритет (из B2BDemo.mount())
    const result = mount.inventorySlug || urlParams.inventorySlug || iframeParams.inventorySlug || runtime.VITE_INVENTORY_SLUG || import.meta.env.VITE_INVENTORY_SLUG || 'demo-autopolis';
    // Use console.warn for production debugging (console.log is stripped by terser)
    console.warn('[RuntimeConfig] INVENTORY_SLUG:', result, {
      mountParams: mount.inventorySlug,
      urlParams: urlParams.inventorySlug,
      iframeParams: iframeParams.inventorySlug,
      runtime: runtime.VITE_INVENTORY_SLUG,
      envFallback: import.meta.env.VITE_INVENTORY_SLUG
    });
    return result;
  },

  // Legacy: для совместимости с car-frontend-api
  // Fallback на INVENTORY_SLUG если INVENTORY_ID не задан явно
  get INVENTORY_ID() {
    const runtime = getRuntimeConfig();
    const urlParams = getUrlParams();
    const iframeParams = getIframeParams();
    return urlParams.inventoryId || iframeParams.inventoryId || runtime.VITE_INVENTORY_ID || import.meta.env.VITE_INVENTORY_ID || this.INVENTORY_SLUG;
  },

  // Dealer logo URL — для лендинга и других branding-чувствительных мест.
  // Подхватывается через mountParams.dealerLogoUrl (когда backend добавит
  // branding.logoUrl в widget config — pipeline в widgetConfigLoader тоже надо
  // расширить).
  //
  // Fallback — логотип самой витрины ShiftGears (#1441). До этого стоял
  // `/assets/landing-logo-badge.png` — логотип Andys Motors, оставшийся с #844:
  // его показывали лендинг машины, куда ведёт QR, и печать страницы машины.
  //
  // Fallback работает ТОЛЬКО в standalone
  // (`demo.shiftgears.ai`) — там shipped asset реально доступен. В widget-mode
  // тот же относительный путь резолвится против host origin (сайт дилера,
  // где `/assets/...` нет) → 404 → broken-image иконка вместо логотипа
  // (issue #897). Поэтому в widget-mode возвращаем null, и CarLandingPage
  // условно скрывает logo-pill — лучше пустое место чем broken-image.
  get DEALER_LOGO_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const explicit = mount.dealerLogoUrl
      || runtime.VITE_DEALER_LOGO_URL
      || import.meta.env.VITE_DEALER_LOGO_URL;
    if (explicit) return explicit;

    const isWidget = (runtime.VITE_ENVIRONMENT_NAME || import.meta.env.VITE_ENVIRONMENT_NAME) === 'widget';
    if (isWidget) return null;

    // Дилерский лендинг по старому QR (`?token`) на том же домене — запасной
    // логотип прежний, как до #1441 (ревью !1301): у такого дилера логотип
    // должен прийти из widget-config, и менять ему запасной мы не собирались.
    if (getUrlParam('token') || mount.frontendToken) return LEGACY_DEALER_LOGO_URL;

    return STANDALONE_LOGO_URL;
  },

  /**
   * Tagline / slogan дилера для блока превью в ShareModal на car detail
   * (issue #957). Пример для Andys:
   *   "Andys Motors™ - Quality Cars | 36 Years of Quality Service"
   *
   * Источники (по приоритету):
   *   1. mount.shareTagline — через `B2BDemo.mount(sel, { shareTagline })`
   *      или widget-config поле (которое прокидывается в b2bMountParams).
   *   2. URL query `?shareTagline=...` — для быстрого testing на demo.
   *   3. env `VITE_SHARE_TAGLINE` — build-time fallback в standalone.
   *
   * Если null — ShareModal скрывает строку с tagline (не рендерит ничего).
   */
  get SHARE_TAGLINE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    if (mount.shareTagline) return mount.shareTagline;
    if (typeof window !== 'undefined') {
      const fromQuery = new URLSearchParams(window.location.search).get('shareTagline');
      if (fromQuery) return fromQuery;
    }
    return runtime.VITE_SHARE_TAGLINE || import.meta.env.VITE_SHARE_TAGLINE || null;
  },

  // Origin сайта дилера, на котором встроен виджет (например `https://andys-motors.com`).
  // Используется как QR-target в print-шаблоне: QR кодирует
  // `${DEALER_SITE_URL}/?widget-view=mini-car&carId=...&token=...` — это ведёт
  // на сам сайт дилера, где встроенный виджет открывает mini-car модалку.
  //
  // Цепочка резолва:
  //   1. mount.dealerSiteUrl — explicit из B2BDemo.mount() или авторезолв
  //      из allowedOrigins[0] токена в widgetConfigLoader.
  //   2. runtime/env — VITE_DEALER_SITE_URL (build-time / docker env).
  //   3. window.location.origin — но ТОЛЬКО в widget mode (виджет встроен
  //      на host page дилера, origin и есть нужный target).
  //   4. null — для standalone print template (живёт на demo.shiftgears.ai,
  //      где origin ≠ сайт дилера; fallback на origin сломал бы QR).
  //      CarPrintTemplate детектит null и кодирует QR в legacy формате.
  //
  // См. issue #878 и meeting #4662 (2026-04-30).
  get DEALER_SITE_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const explicit = mount.dealerSiteUrl
      || runtime.VITE_DEALER_SITE_URL
      || import.meta.env.VITE_DEALER_SITE_URL;
    if (explicit) return explicit;

    // Widget mode — host page это и есть сайт дилера. В standalone (print)
    // ENVIRONMENT_NAME ≠ 'widget' → fallback не применяем, возвращаем null.
    const isWidget = (runtime.VITE_ENVIRONMENT_NAME || import.meta.env.VITE_ENVIRONMENT_NAME) === 'widget';
    if (isWidget && typeof window !== 'undefined') {
      return window.location.origin;
    }
    return null;
  },

  // Полный URL каталога дилера (origin + path до catalog page).
  // Используется как QR-target вместо `DEALER_SITE_URL` для случая когда
  // виджет на host page mount-ится в `full` mode только на конкретной
  // странице (типичный embed-script через MODX footer chunk:
  // `mode: pathname === '/used-cars' ? 'full' : 'chat-only'`).
  //
  // Без catalog-path в QR пользователь после скана попадает на корень
  // сайта дилера, виджет mount-ится в chat-only, после закрытия модалки
  // юзер видит home page вместо каталога (issue #897).
  //
  // Цепочка резолва:
  //   1. mount.dealerCatalogUrl — explicit полный URL.
  //   2. mount.dealerCatalogPath — explicit path, склеивается с DEALER_SITE_URL.
  //   3. WEBSITE_PATH_PREFIX — для случая когда сайт дилера mounted на
  //      sub-path (dev.eezyworks.com/andys/sg-used-cars), prefix и есть
  //      путь до каталога.
  //   4. WEBSITE_PAGES — ищем страницу с id, соответствующим catalog.
  //   5. null — DEALER_SITE_URL без catalog path (legacy QR target).
  get DEALER_CATALOG_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();

    if (mount.dealerCatalogUrl) return mount.dealerCatalogUrl;
    if (runtime.VITE_DEALER_CATALOG_URL) return runtime.VITE_DEALER_CATALOG_URL;
    if (import.meta.env.VITE_DEALER_CATALOG_URL) return import.meta.env.VITE_DEALER_CATALOG_URL;

    const siteUrl = this.DEALER_SITE_URL;
    if (!siteUrl) return null;

    // Если siteUrl УЖЕ содержит path (не просто origin) — это полный
    // catalog URL, ничего добавлять не нужно. На test для Andys backend
    // отдаёт `primaryOrigin = https://dev.eezyworks.com/andys/sg-used-cars`
    // (full URL включая path до catalog-page, потому что allowedOrigins
    // CORS-валидируется по полному URL). Если бы мы поверх этого добавили
    // ещё `WEBSITE_PATH_PREFIX = '/andys'` — получили бы
    // `/andys/sg-used-cars/andys` (двойной prefix), который embed-script
    // на eezyworks не распознал бы как catalog → mount в chat-only,
    // модалка не открывается (#897 регрессия — поймана на test после
    // первого деплоя).
    let parsedSiteUrl;
    try {
      parsedSiteUrl = new URL(siteUrl);
    } catch (e) {
      console.warn('[RuntimeConfig] DEALER_SITE_URL is malformed, skipping catalog path composition:', siteUrl);
      return siteUrl;
    }

    const sitePath = parsedSiteUrl.pathname.replace(/\/+$/, '');
    if (sitePath && sitePath !== '') {
      // siteUrl уже catalog URL — используем как есть.
      return siteUrl;
    }

    // siteUrl это голый origin → склеиваем с catalog path.
    const catalogPath = (() => {
      // Explicit path mount param
      if (mount.dealerCatalogPath) return mount.dealerCatalogPath;

      // WEBSITE_PATH_PREFIX уже резолвится с очищенным trailing slash.
      // Используем только если origin без path (см. siteUrl-проверку выше).
      const prefix = this.WEBSITE_PATH_PREFIX;
      if (prefix) return prefix;

      // Поиск catalog-страницы в WEBSITE_PAGES.
      // Heuristic: id matches /used-cars|inventory|catalog/ либо path содержит эти слова.
      const pages = this.WEBSITE_PAGES;
      const catalogRegex = /^(used-cars|inventory|catalog)$/i;
      const found = pages.find((p) => catalogRegex.test(p.id || ''));
      if (found?.path) return found.path;

      return '';
    })();

    if (!catalogPath) return siteUrl;

    // Нормализуем slash'ы при склейке: origin может или не может
    // заканчиваться на /, path может или не может начинаться с /.
    const cleanOrigin = siteUrl.replace(/\/+$/, '');
    const cleanPath = catalogPath.startsWith('/') ? catalogPath : `/${catalogPath}`;
    return `${cleanOrigin}${cleanPath}`;
  },

  // Полный URL страницы аренды дилера (Rent-CTA лендинга уводит СЮДА).
  // ORIGIN-AWARE (#1252): test-лендинг → test `/car-rental` (тот же env →
  // тот же widget-config + виджет + флаг), а НЕ хардкодом на прод
  // `andys-motors.com/car-rental` (из-за него на test data не прокидывались:
  // уходили на прод-конфиг, где флаг off / бандл старее).
  //
  // В отличие от DEALER_CATALOG_URL (там DEALER_SITE_URL САМ и есть catalog
  // page) — rental это ДРУГАЯ страница, поэтому берём ORIGIN сайта + sub-path
  // prefix + rental path из WEBSITE_PAGES.
  //
  // Цепочка: mount.dealerRentalUrl → env → origin(DEALER_SITE_URL) + prefix +
  // rental page path → null (тогда caller фолбэчится на хардкод-прод).
  get DEALER_RENTAL_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();

    if (mount.dealerRentalUrl) return mount.dealerRentalUrl;
    if (runtime.VITE_DEALER_RENTAL_URL) return runtime.VITE_DEALER_RENTAL_URL;
    if (import.meta.env.VITE_DEALER_RENTAL_URL) return import.meta.env.VITE_DEALER_RENTAL_URL;

    const siteUrl = this.DEALER_SITE_URL;
    if (!siteUrl) return null;

    // Путь до rental-страницы из WEBSITE_PAGES (id car-rental|rental, либо path
    // содержит car-rental); иначе дефолт '/car-rental'. Композиция origin +
    // prefix + path — в чистом composeDealerPageUrl (тестируется отдельно).
    const rentalPage = this.WEBSITE_PAGES.find(
      (p) => /^(car-rental|rental)$/i.test(p.id || '') || /car-rental/i.test(p.path || ''),
    );
    return composeDealerPageUrl(siteUrl, this.WEBSITE_PATH_PREFIX, rentalPage?.path || '/car-rental');
  },

  // Полный URL страницы лизинга дилера («Browse all» на Lease-табе уводит СЮДА).
  // ORIGIN-AWARE по тем же причинам, что DEALER_RENTAL_URL (#1252): с test-
  // лендинга ведём на test `/leasing/`, а не хардкодом на прод. Раньше здесь был
  // хардкод ANDYS_LEASING_URL в контейнере (#1240/#1248) — ровно тот класс бага,
  // что ловили в #1252 на rental.
  //
  // Цепочка: mount.dealerLeasingUrl → env → origin(DEALER_SITE_URL) + prefix +
  // leasing page path → null (тогда caller фолбэчится на хардкод-прод).
  get DEALER_LEASING_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();

    if (mount.dealerLeasingUrl) return mount.dealerLeasingUrl;
    if (runtime.VITE_DEALER_LEASING_URL) return runtime.VITE_DEALER_LEASING_URL;
    if (import.meta.env.VITE_DEALER_LEASING_URL) return import.meta.env.VITE_DEALER_LEASING_URL;

    const siteUrl = this.DEALER_SITE_URL;
    if (!siteUrl) return null;

    const leasingPage = this.WEBSITE_PAGES.find(
      (p) => /^leasing$/i.test(p.id || '') || /leasing/i.test(p.path || ''),
    );
    return composeDealerPageUrl(siteUrl, this.WEBSITE_PATH_PREFIX, leasingPage?.path || '/leasing');
  },

  // Agent параметры (no workflow since gitlab#1576)
  get AGENT_ID() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const urlParams = getUrlParams();
    const iframeParams = getIframeParams();
    // mountParams имеет НАИВЫСШИЙ приоритет (из B2BDemo.mount() или widget config)
    return mount.agentId || urlParams.agentId || iframeParams.agentId || runtime.VITE_AGENT_ID || import.meta.env.VITE_AGENT_ID || null;
  },

  // Voice Assistant
  get VOICE_ASSISTANT_PROVIDER() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_VOICE_ASSISTANT_PROVIDER || import.meta.env.VITE_VOICE_ASSISTANT_PROVIDER || 'livekit';
  },
  get LIVEKIT_CONN_DETAILS_ENDPOINT() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_LIVEKIT_CONN_DETAILS_ENDPOINT || import.meta.env.VITE_LIVEKIT_CONN_DETAILS_ENDPOINT || 'https://cloud-api.livekit.io/api/sandbox/connection-details';
  },
  get ENVIRONMENT_NAME() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_ENVIRONMENT_NAME || import.meta.env.VITE_ENVIRONMENT_NAME || 'development';
  },

  // Sentry Error Monitoring
  // DSN and environment can be provided via runtimeConfig, env vars, or B2BDemo.mount()
  // NOTE: NOT read from URL params or iframe data-attributes for security reasons
  get SENTRY_DSN() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_SENTRY_DSN || import.meta.env.VITE_SENTRY_DSN || null;
  },
  get SENTRY_ENVIRONMENT() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_SENTRY_ENVIRONMENT || null;
  },
  get SENTRY_RELEASE() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_SENTRY_RELEASE || import.meta.env.VITE_SENTRY_RELEASE || null;
  },

  // Widget theme customization
  // WIDGET_STYLE: 'shiftgears' | 'default' | 'andys' | 'autodis'
  // ⛔ Дефолт ниже — 'demo-shift-gears', легаси-оранжевый: это фактический цвет
  // виджета у всех тенантов без appearance.style, поэтому менять его нельзя без
  // инвентаризации (#1324). Новый код берёт 'shiftgears' — см. themes.js.
  // mountParams.widgetStyle имеет высший приоритет — populated by
  // showroomTokenResolver из widget-config API. Без него standalone
  // (landing/print) был бы зашит на demo-shift-gears даже для Andys.
  get WIDGET_STYLE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.widgetStyle || runtime.VITE_WIDGET_STYLE || import.meta.env.VITE_WIDGET_STYLE || 'demo-shift-gears';
  },
  // WIDGET_THEME_MODE: 'light' | 'dark'
  get WIDGET_THEME_MODE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.widgetThemeMode || runtime.VITE_WIDGET_THEME_MODE || import.meta.env.VITE_WIDGET_THEME_MODE || 'light';
  },
  // WIDGET_ACCENT: use primary color as background for voice/collapsed modes
  get WIDGET_ACCENT() {
    const mount = getMountParams();
    if (mount.widgetAccent !== undefined) return mount.widgetAccent === true || mount.widgetAccent === 'true';
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_WIDGET_ACCENT || import.meta.env.VITE_WIDGET_ACCENT;
    return value === 'true' || value === true;
  },
  // WIDGET_GLASS: enable glassmorphism effect
  get WIDGET_GLASS() {
    const mount = getMountParams();
    if (mount.widgetGlass !== undefined) return mount.widgetGlass === true || mount.widgetGlass === 'true';
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_WIDGET_GLASS || import.meta.env.VITE_WIDGET_GLASS;
    // Default to true if not specified
    if (value === undefined || value === null || value === '') return true;
    return value === 'true' || value === true;
  },

  // Search bar visibility on catalog page (default: false — enable via widget-config)
  get SHOW_SEARCH_BAR() {
    const mount = getMountParams();
    if (mount.showSearchBar === true) return true;
    return false;
  },

  /**
   * Client-side **requested** search mode for the SearchBar. This is NOT
   * authoritative — the b2b-api server is the source of truth via its own
   * `MEILI_SEARCH_MODE` env. The flow is:
   *
   *   1. b2b-api (`MEILI_SEARCH_MODE` env) — final authority. Decides the
   *      `mode` field in every /api/search response.
   *   2. b2b-demo widget config (this getter) — what the client *would like*.
   *      The server may downgrade hybrid → keyword or kill the request
   *      entirely with `mode: 'disabled'`.
   *
   * Values:
   *   - 'disabled' : SHOW_SEARCH_BAR is false → SearchBar is not rendered;
   *     api.js short-circuits any leftover calls (defensive frontend stub).
   *   - 'keyword'  : SearchBar visible, frontend sends a plain query.
   *   - 'hybrid'   : SearchBar visible, frontend opts in by appending
   *     `?hybrid=true`. The server still has the final word.
   *
   * Driven by widget config (b2bMountParams.searchBarMode), set per-tenant
   * via Demo Showcase → Settings → Search Bar / Search Mode.
   */
  get WIDGET_SEARCH_MODE() {
    if (!config.SHOW_SEARCH_BAR) return 'disabled';
    const mount = getMountParams();
    const raw = (mount.searchBarMode || '').toString().trim().toLowerCase();
    if (raw === 'hybrid') return 'hybrid';
    return 'keyword';
  },

  /**
   * Raw search bar mode preference, **независимо** от `SHOW_SEARCH_BAR`.
   *
   * Используется force-bypass путём в `searchCarInventory({ force: true })`
   * (Search registry component, search-only embed) — чтобы admin мог получить
   * hybrid-режим для search-only embed без обязательного включения global
   * `showSearchBar` toggle (который активирует in-catalog SearchBar в
   * `<CarsPage>`). См. issue #932 — full decoupling.
   *
   * `WIDGET_SEARCH_MODE` остаётся authority для legacy in-catalog SearchBar
   * (gated by SHOW_SEARCH_BAR). `SEARCH_BAR_MODE_RAW` — для callers которые
   * УЖЕ прошли свой собственный gate (marker presence = explicit opt-in).
   *
   * Values:
   *   - 'hybrid'  : `?hybrid=true&semanticRatio=0.5` опт-ин
   *   - 'keyword' : default fallback (включая случаи когда searchBarMode не задан)
   *
   * НЕ возвращает 'disabled' — caller-ы этого getter-а УЖЕ решили, что
   * запрос обязателен (gate на их стороне).
   */
  get SEARCH_BAR_MODE_RAW() {
    const mount = getMountParams();
    const raw = (mount.searchBarMode || '').toString().trim().toLowerCase();
    if (raw === 'hybrid') return 'hybrid';
    return 'keyword';
  },

  // Preset filters for embedded catalog (Special Offers, British Forces, etc.)
  // Set via B2BDemo.mount(sel, { presetFilters: { isHotOffer: true } })
  get PRESET_FILTERS() {
    const mount = getMountParams();
    return mount.presetFilters || null;
  },

  // ─── Mini-Catalog mode (#828) ───────────────────────────────────────────
  // Компактный каталог без чата для встраивания на главную/промо-страницы
  // клиентского сайта. Выборка задаётся через inline presetFilters (уже есть
  // из #783); mini-catalog добавляет лимит карточек и hard redirect при клике.

  /**
   * Максимум машин в mini-catalog витрине. Если null — без лимита (весь sorted список).
   * Clamped в диапазоне [1, 50] на safety; валидные значения для UX — 3..12.
   */
  get MAX_CARS() {
    const mount = getMountParams();
    if (mount.maxCars === null || mount.maxCars === undefined || mount.maxCars === '') return null;
    const n = Number(mount.maxCars);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.min(Math.max(Math.round(n), 1), 50);
  },

  /**
   * Base URL каталога клиентского сайта для hard redirect при клике на карточку.
   * Напр. 'https://andys-motors.com/used-cars' → click становится
   * window.location.href = 'https://andys-motors.com/used-cars#/cars/<id>'.
   * Если не задан — клик работает как в обычном режиме (internal navigate).
   */
  get CATALOG_URL() {
    const mount = getMountParams();
    return typeof mount.catalogUrl === 'string' && mount.catalogUrl.length > 0
      ? mount.catalogUrl
      : null;
  },

  // Header/Footer visibility (default: hidden in widget mode, shown in standalone mode)
  get SHOW_HEADER() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_SHOW_HEADER || import.meta.env.VITE_SHOW_HEADER;
    // В widget mode (ENVIRONMENT_NAME === 'widget') по умолчанию скрываем
    // В standalone mode по умолчанию показываем
    if (value === 'true') return true;
    if (value === 'false') return false;
    // Default: show in standalone, hide in widget
    return runtime.VITE_ENVIRONMENT_NAME !== 'widget';
  },
  get SHOW_FOOTER() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_SHOW_FOOTER || import.meta.env.VITE_SHOW_FOOTER;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return runtime.VITE_ENVIRONMENT_NAME !== 'widget';
  },

  // Хост, с которого окружение раздаёт фото демо-стока (#1416). Задан только
  // у тестовой витрины (helm values.test.yaml); пусто — ссылки идут как есть.
  // См. utils/demoAssets.js.
  get DEMO_ASSETS_ORIGIN() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_DEMO_ASSETS_ORIGIN || import.meta.env.VITE_DEMO_ASSETS_ORIGIN || '';
  },

  // Header height for sticky filters positioning (in pixels)
  // Default: 64 when header is shown, 100 when embedded (for parent site header)
  get HEADER_HEIGHT() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // mountParams имеет НАИВЫСШИЙ приоритет
    if (mount.headerHeight != null) return mount.headerHeight;
    const envValue = runtime.VITE_HEADER_HEIGHT || import.meta.env.VITE_HEADER_HEIGHT;
    if (envValue != null) return parseInt(envValue, 10);
    // Default: 64px if header shown, 100px if embedded (typical parent header height)
    return this.SHOW_HEADER ? 64 : 100;
  },

  // Widget mode configuration
  // WIDGET_MODE: 'full' (чат + каталог) | 'chat-only' (только чат) | 'catalog-only' (только каталог)
  get WIDGET_MODE() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_WIDGET_MODE || import.meta.env.VITE_WIDGET_MODE || 'full';
  },

  // ACTIVATION_KEY: ключ localStorage для активации виджета
  // null = виджет всегда активен, иначе проверяет наличие записи в localStorage
  get ACTIVATION_KEY() {
    const runtime = getRuntimeConfig();
    // Если явно установлено null/undefined в runtime, возвращаем null
    if (runtime.VITE_ACTIVATION_KEY === null) return null;
    return runtime.VITE_ACTIVATION_KEY || import.meta.env.VITE_ACTIVATION_KEY || 'shift_gears_enabled';
  },

  // HIDE_SELECTOR: CSS селектор(ы) элементов для скрытия при активации виджета
  // Например, для скрытия конкурирующих виджетов (WhatsApp и т.п.)
  get HIDE_SELECTOR() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_HIDE_SELECTOR ?? import.meta.env.VITE_HIDE_SELECTOR ?? null;
  },

  // CATALOG_PATH_PATTERN: RegExp pattern для определения страниц с каталогом
  // На этих страницах каталог загружается автоматически в chat-only режиме
  get CATALOG_PATH_PATTERN() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_CATALOG_PATH_PATTERN || import.meta.env.VITE_CATALOG_PATH_PATTERN || '/cars|/inventory';
  },

  // ACTIVATION_MODE: режим активации виджета ('always' | 'query-param' | 'localStorage')
  get ACTIVATION_MODE() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_ACTIVATION_MODE || import.meta.env.VITE_ACTIVATION_MODE || 'always';
  },

  // ACTIVATION_QUERY_PARAM_NAME: имя query параметра для активации виджета
  get ACTIVATION_QUERY_PARAM_NAME() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_ACTIVATION_QUERY_PARAM_NAME || import.meta.env.VITE_ACTIVATION_QUERY_PARAM_NAME || 'shiftgears-enabled';
  },

  // ACTIVATION_QUERY_PARAM_VALUE: значение query параметра для активации виджета
  get ACTIVATION_QUERY_PARAM_VALUE() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_ACTIVATION_QUERY_PARAM_VALUE || import.meta.env.VITE_ACTIVATION_QUERY_PARAM_VALUE || 'true';
  },

  // CHECK_AVAILABILITY_AI_CONSULTATION_ENABLED: показывать ли AI Consultation CTA в success-state формы Check Availability.
  // Greeting текст НЕ хранится тут — он в b2b_agent default_phrases.py, выбирается по launch_context.action='check_availability'.
  get CHECK_AVAILABILITY_AI_CONSULTATION_ENABLED() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.checkAvailabilityAiConsultationEnabled ?? runtime.VITE_CHECK_AVAILABILITY_AI_CONSULTATION_ENABLED ?? import.meta.env.VITE_CHECK_AVAILABILITY_AI_CONSULTATION_ENABLED;
    if (value === true || value === 'true') return true;
    return false;
  },

  // CAPTURE_FORMS_TO_CRM: дублировать submit форм виджета (Check Availability, Finance)
  // в наш CRM через POST /api/widget/lead-capture параллельно с MODX Formit submit.
  // Default: false (включать per-org по мере rollout). См. apps/b2b-demo/src/services/api.js#submitLeadFromForm.
  get CAPTURE_FORMS_TO_CRM() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.captureFormsToCrm ?? runtime.VITE_CAPTURE_FORMS_TO_CRM ?? import.meta.env.VITE_CAPTURE_FORMS_TO_CRM;
    if (value === true || value === 'true') return true;
    return false;
  },

  // ─── Standalone config-driven form widget (#1081) ───────────────────────
  // `<div data-shiftgears="form">` renders <LeadFormEmbed>, captures a lead to
  // CRM, then morphs form→chat (AI handover). These getters read the whitelisted
  // window.b2bMountParams.form* fields (mapped from apiConfig.formConfig in
  // widgetConfigLoader). LeadFormEmbed itself prefers per-embed marker `data-*`
  // attrs over these globals.

  // FORM_TITLE: heading above the form. Default provided by LeadFormEmbed.
  get FORM_TITLE() {
    const mount = getMountParams();
    return typeof mount.formTitle === 'string' && mount.formTitle.length > 0 ? mount.formTitle : null;
  },

  // FORM_SUBMIT_LABEL: submit button text. Default provided by LeadFormEmbed.
  get FORM_SUBMIT_LABEL() {
    const mount = getMountParams();
    return typeof mount.formSubmitLabel === 'string' && mount.formSubmitLabel.length > 0
      ? mount.formSubmitLabel
      : null;
  },

  // FORM_TYPE: WidgetFormType sent to /api/widget/lead-capture.
  // Backend accepts contact|enquiry|check_availability|finance|test_drive.
  get FORM_TYPE() {
    const mount = getMountParams();
    const raw = (mount.formType || '').toString().trim();
    const allowed = ['contact', 'enquiry', 'check_availability', 'finance', 'test_drive'];
    return allowed.includes(raw) ? raw : 'contact';
  },

  // FORM_DEFAULT_CHANNEL: preselected preferred channel radio (phone|email|whatsapp).
  get FORM_DEFAULT_CHANNEL() {
    const mount = getMountParams();
    const raw = (mount.formDefaultChannel || '').toString().trim();
    return ['phone', 'email', 'whatsapp'].includes(raw) ? raw : 'phone';
  },

  // FORM_PREFERRED_CHANNELS: which channel options are offered. Array of
  // 'phone'|'email'|'whatsapp'. Empty/absent → hide the chooser.
  get FORM_PREFERRED_CHANNELS() {
    const mount = getMountParams();
    const raw = mount.formPreferredChannels;
    const list = Array.isArray(raw) ? raw : [];
    const filtered = list.filter((c) => ['phone', 'email', 'whatsapp'].includes(c));
    return filtered.length > 0 ? filtered : null;
  },

  // FORM_SHOW_CHANNEL_CHOOSER: master toggle for the preferred-channel radio.
  get FORM_SHOW_CHANNEL_CHOOSER() {
    const mount = getMountParams();
    const value = mount.formShowChannelChooser;
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    // Default: show if channels are configured.
    return Array.isArray(mount.formPreferredChannels) && mount.formPreferredChannels.length > 0;
  },

  // FORM_FIELDS: which core fields to render, e.g. ['name','phone','email','message'].
  // Absent → LeadFormEmbed uses its own default set.
  get FORM_FIELDS() {
    const mount = getMountParams();
    return Array.isArray(mount.formFields) && mount.formFields.length > 0 ? mount.formFields : null;
  },

  // FORM_CUSTOM_FIELDS: config-declared extra fields collected into `extra`.
  // Each: { name, label, type?, required?, options? }.
  get FORM_CUSTOM_FIELDS() {
    const mount = getMountParams();
    return Array.isArray(mount.formCustomFields) && mount.formCustomFields.length > 0
      ? mount.formCustomFields
      : null;
  },

  // FORM_SUCCESS_BANNER: persistent "saved as lead" banner copy.
  get FORM_SUCCESS_BANNER() {
    const mount = getMountParams();
    return typeof mount.formSuccessBanner === 'string' && mount.formSuccessBanner.length > 0
      ? mount.formSuccessBanner
      : null;
  },

  // FORM_MORPH_TO_CHAT: after capture, morph form→chat (AI handover). Default true.
  get FORM_MORPH_TO_CHAT() {
    const mount = getMountParams();
    const value = mount.formMorphToChat;
    if (value === false || value === 'false') return false;
    return true;
  },

  // FORM_HANDOVER_CTA_TEXT: label of the soft "try the assistant again" button
  // shown in the degraded (chat unavailable) confirmation state.
  get FORM_HANDOVER_CTA_TEXT() {
    const mount = getMountParams();
    return typeof mount.formHandoverCtaText === 'string' && mount.formHandoverCtaText.length > 0
      ? mount.formHandoverCtaText
      : null;
  },

  // FORM_SUCCESS_PARAM: `key=value` marker appended to the page URL on a
  // successful submit for PPC conversion tracking (#1162). Default form=success
  // (applied by LeadFormEmbed). Per-embed `data-form-success-param` wins.
  // Stays in the URL for about a second only (#1566); the durable signal is the
  // dataLayer `lead_submitted` event + Meta `Lead`.
  get FORM_SUCCESS_PARAM() {
    const mount = getMountParams();
    return typeof mount.formSuccessParam === 'string' && mount.formSuccessParam.length > 0
      ? mount.formSuccessParam
      : null;
  },

  // FORM_TEMPLATES: resolved form-template registry (#1087 / #1123) — built-in
  // registry ⊕ org overrides, served at the top level of the public widget
  // config (`formTemplates`) and threaded here via b2bMountParams. LeadFormEmbed
  // resolves a template BY CODE (`data-form-template` / the car page's passed
  // code) against this array. Empty array when the config carries none (older
  // backend / no token) — resolve-by-code then falls through to legacy/defaults.
  get FORM_TEMPLATES() {
    const mount = getMountParams();
    return Array.isArray(mount.formTemplates) ? mount.formTemplates : [];
  },

  // NOTE: no FORM_MODX_ENDPOINT getter. The optional MODX FormIt POST target is
  // supplied PER-EMBED via the `data-form-modx-endpoint` marker attr (dual-post
  // is a per-page concern), which `resolveFormConfig` reads straight off attrs.
  // There is no org-level `modxEndpoint` in the WidgetFormConfig contract, so a
  // global mount-param fallback would have no source — the getter was dead and
  // is removed (#1081 review). Add a global path only if the shared type grows
  // a `modxEndpoint` field.

  // WEBSITE_PATH_PREFIX: префикс пути для навигации по сайту клиента
  // Например: '/andys' если сайт размещён на eezyworks.com/andys/
  // Передаётся через mount params (websitePathPrefix) или env var (VITE_WEBSITE_PATH_PREFIX)
  get WEBSITE_PATH_PREFIX() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return (mount.websitePathPrefix || runtime.VITE_WEBSITE_PATH_PREFIX || import.meta.env.VITE_WEBSITE_PATH_PREFIX || '').replace(/\/+$/, '');
  },

  // WEBSITE_PAGES: массив страниц сайта клиента для website navigation
  // Формат: [{ id: 'contact', label: 'Contact Us', path: '/contact-us', description: 'Contact page' }, ...]
  // Может передаваться через mount config (websitePages) или через VITE_WEBSITE_PAGES (JSON строка)
  // Если задан WEBSITE_PATH_PREFIX, он автоматически добавляется ко всем path
  get WEBSITE_PAGES() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    let pages = [];

    // Приоритет: mountParams > runtimeConfig > env > hardcoded fallback
    if (mount.websitePages && Array.isArray(mount.websitePages)) {
      pages = mount.websitePages;
    } else {
      // Пробуем распарсить JSON из runtime или env
      const jsonStr = runtime.VITE_WEBSITE_PAGES || import.meta.env.VITE_WEBSITE_PAGES;
      if (jsonStr) {
        try {
          const parsed = JSON.parse(jsonStr);
          if (Array.isArray(parsed)) {
            pages = parsed;
          }
        } catch (e) {
          console.warn('[RuntimeConfig] Failed to parse WEBSITE_PAGES JSON:', e);
        }
      }

      // Hardcoded fallback для andys-motors.com
      if (pages.length === 0 && typeof window !== 'undefined' && window.location.hostname === 'andys-motors.com') {
        pages = ANDYS_MOTORS_WEBSITE_PAGES;
      }
    }

    // Применяем WEBSITE_PATH_PREFIX ко всем path
    const prefix = this.WEBSITE_PATH_PREFIX;
    if (prefix && pages.length > 0) {
      return pages.map(p => ({ ...p, path: prefix + p.path }));
    }

    return pages;
  },

  // DUTY_FREE_PRICES: показывать Duty Free цены (для British Forces/Serving Personnel)
  // Duty Free цена = обычная цена без VAT (19% на Кипре)
  get SHOW_DUTY_FREE_PRICES() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.showDutyFreePrices || runtime.VITE_SHOW_DUTY_FREE_PRICES || import.meta.env.VITE_SHOW_DUTY_FREE_PRICES;
    // Default to false
    return value === 'true' || value === true;
  },
  // SHOW_DUTY_FREE_FILTER: показывать Duty Free checkbox в фильтрах на странице каталога.
  // Читает mount params (#990). DF-настройки приходят в ДВУХ формах, обе валидны:
  //  - nested `dutyFreeSettings` — token/marker путь (mapApiConfigToMountConfig /
  //    widgetConfigLoader кладут вложенный объект; window.shiftgearsConfig embed);
  //  - flat `dutyFreeLabel`/`dutyFreeVatRate`/... — inline B2BDemo.mount config
  //    (его генерит demo-showcase form и copy-paste сниппет). bootstrap.jsx спредит
  //    весь config в b2bMountParams КАК ЕСТЬ (не нормализует), поэтому getters ниже
  //    читают оба варианта, иначе кастомный label/VAT молча падает в дефолт.
  get SHOW_DUTY_FREE_FILTER() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.showDutyFreePrices || mount.dutyFreeSettings?.enabled || runtime.VITE_SHOW_DUTY_FREE_FILTER || import.meta.env.VITE_SHOW_DUTY_FREE_FILTER;
    // Default to false
    return value === 'true' || value === true;
  },
  // SHOW_CLASSIFIEDS_SYNC: строка «эта же машина на площадках» на карточке авто
  // (#1338). Демо-онли: `CarDetailPage` едет дилерам через registry/Catalog,
  // и без флага блок появился бы у каждого клиента. Дефолт `false`.
  get SHOW_CLASSIFIEDS_SYNC() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.showClassifiedsSync ?? runtime.VITE_SHOW_CLASSIFIEDS_SYNC ?? import.meta.env.VITE_SHOW_CLASSIFIEDS_SYNC;
    return value === true || value === 'true';
  },
  // SHOW_FINANCE_CALCULATOR: раскрывать блок расчёта платежа на странице авто
  // БЕЗ тогла Duty Free (#1335).
  //
  // Блок платежа исторически принадлежит British Forces: он и показывается по
  // `SHOW_BF_PACKAGE`, и раскрывается только когда включён Duty Free — у BF это
  // одна и та же покупка. Витрине нужен обычный калькулятор без военной
  // семантики, поэтому раскрытие вынесено в отдельный флаг.
  //
  // Дефолт `false` — у дилеров поведение прежнее: блок раскрывает Duty Free и
  // ничто иное.
  get SHOW_FINANCE_CALCULATOR() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.showFinanceCalculator ?? runtime.VITE_SHOW_FINANCE_CALCULATOR ?? import.meta.env.VITE_SHOW_FINANCE_CALCULATOR;
    return value === true || value === 'true';
  },
  // SHOW_BF_PACKAGE: показывать блок British Forces Exclusive Package на странице авто
  get SHOW_BF_PACKAGE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // mountParams имеет НАИВЫСШИЙ приоритет
    const value = mount.showBfPackage ?? runtime.VITE_SHOW_BF_PACKAGE ?? import.meta.env.VITE_SHOW_BF_PACKAGE;
    // Default to true (показываем блок если не задано явно)
    if (value === undefined || value === null) return true;
    return value === 'true' || value === true;
  },
  // SHOW_SALE_BADGE: показывать зелёный бейдж "SALE" на карточках со сниженной ценой
  get SHOW_SALE_BADGE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.showSaleBadge ?? runtime.VITE_SHOW_SALE_BADGE ?? import.meta.env.VITE_SHOW_SALE_BADGE;
    // Default to true (показываем бейдж если не задано явно)
    if (value === undefined || value === null) return true;
    return value === 'true' || value === true;
  },
  // SHOW_WHY_BUY_BLOCK: показывать блок "Why BUY from Us" на странице авто
  get SHOW_WHY_BUY_BLOCK() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // mountParams имеет НАИВЫСШИЙ приоритет
    const value = mount.showWhyBuyBlock || runtime.VITE_SHOW_WHY_BUY_BLOCK || import.meta.env.VITE_SHOW_WHY_BUY_BLOCK;
    // Default to false
    return value === 'true' || value === true;
  },
  // DUTY_FREE_VAT_RATE: VAT ставка для расчёта Duty Free цен (по умолчанию 19% для Кипра)
  get DUTY_FREE_VAT_RATE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.dutyFreeSettings?.vatRate || mount.dutyFreeVatRate || runtime.VITE_DUTY_FREE_VAT_RATE || import.meta.env.VITE_DUTY_FREE_VAT_RATE;
    return value ? parseFloat(value) : 0.19; // 19% VAT по умолчанию
  },
  // DUTY_FREE_LABEL: текст label для checkbox/toggle (например, "Duty Free Prices" или "British Forces Duty Free Prices")
  get DUTY_FREE_LABEL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.dutyFreeSettings?.label || mount.dutyFreeLabel || runtime.VITE_DUTY_FREE_LABEL || import.meta.env.VITE_DUTY_FREE_LABEL || 'British Forces Duty Free Prices';
  },
  // DUTY_FREE_TOOLTIP_TITLE: заголовок tooltip
  get DUTY_FREE_TOOLTIP_TITLE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.dutyFreeSettings?.tooltipTitle || mount.dutyFreeTooltipTitle || runtime.VITE_DUTY_FREE_TOOLTIP_TITLE || import.meta.env.VITE_DUTY_FREE_TOOLTIP_TITLE || 'British Forces Duty Free Prices';
  },
  // DUTY_FREE_TOOLTIP_TEXT: основной текст tooltip
  get DUTY_FREE_TOOLTIP_TEXT() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.dutyFreeSettings?.tooltipText || mount.dutyFreeTooltipText || runtime.VITE_DUTY_FREE_TOOLTIP_TEXT || import.meta.env.VITE_DUTY_FREE_TOOLTIP_TEXT ||
      'For British Forces/Serving Personnel. Prices shown exclude 19% VAT.';
  },
  // DUTY_FREE_BUTTON_TEXT: текст кнопки на странице детали авто
  get DUTY_FREE_BUTTON_TEXT() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.dutyFreeSettings?.buttonText || mount.dutyFreeButtonText || runtime.VITE_DUTY_FREE_BUTTON_TEXT || import.meta.env.VITE_DUTY_FREE_BUTTON_TEXT || 'Show Duty Free Price';
  },
  // DUTY_FREE_PRICE_LABEL: короткий лейбл рядом с ценой (например, "DF" или "Duty Free")
  get DUTY_FREE_PRICE_LABEL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.dutyFreeSettings?.priceLabel || mount.dutyFreePriceLabel || runtime.VITE_DUTY_FREE_PRICE_LABEL || import.meta.env.VITE_DUTY_FREE_PRICE_LABEL || 'DF';
  },

  // SUGGESTIONS: настройки авто-предложений в чате (#752)
  get SUGGESTIONS_ENABLED() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // Env-флаг — только для своей витрины (#1569): он лежит в runtime-config
    // демо-домена, а дилер, открытый там по ?token=, решает по своему
    // widget-config, а не по флагу витрины.
    const envValue = this.IS_OWN_STOCK
      ? runtime.VITE_SUGGESTIONS_ENABLED ?? import.meta.env.VITE_SUGGESTIONS_ENABLED
      : undefined;
    const value = mount.suggestionsEnabled ?? envValue;
    // Feature flag (#752): default to FALSE — enable per-org via widget config UI when ready.
    // Safe to merge to main without blocking prod rollout.
    if (value === undefined || value === null) return false;
    return value === 'true' || value === true;
  },
  get SUGGESTIONS_MAX_VISIBLE() {
    // #886: return `undefined` when no source provided so callers can
    // distinguish "not configured by org" from "explicit value". The
    // request-side (useTextChat) skips `params.max_suggestions` entirely
    // in that case → backend falls back to schema MAX_SUGGESTIONS.
    // The UI-side (Suggestions component) has its own default `maxVisible = 3`.
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.suggestionsMaxVisible || runtime.VITE_SUGGESTIONS_MAX_VISIBLE || import.meta.env.VITE_SUGGESTIONS_MAX_VISIBLE;
    return value ? parseInt(value, 10) : undefined;
  },
  get SUGGESTIONS_ACCENT_COLOR() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.suggestionsAccentColor || runtime.VITE_SUGGESTIONS_ACCENT_COLOR || import.meta.env.VITE_SUGGESTIONS_ACCENT_COLOR || null;
  },
  get SUGGESTIONS_BUBBLE_VARIANT() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.suggestionsBubbleVariant || runtime.VITE_SUGGESTIONS_BUBBLE_VARIANT || import.meta.env.VITE_SUGGESTIONS_BUBBLE_VARIANT || 'outlined';
  },

  // British Forces Exclusive Package configuration
  // BF_PACKAGE_TITLE: заголовок блока (например, "BRITISH FORCES EXCLUSIVE PACKAGE")
  // Используйте \n для переноса строки
  get BF_PACKAGE_TITLE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfPackageTitle || runtime.VITE_BF_PACKAGE_TITLE || import.meta.env.VITE_BF_PACKAGE_TITLE || 'British Forces\nExclusive Package';
  },
  // BF_PACKAGE_PCM_TEXT: текст с ежемесячным платежом (используйте {pcm} как placeholder)
  get BF_PACKAGE_PCM_TEXT() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_BF_PACKAGE_PCM_TEXT || import.meta.env.VITE_BF_PACKAGE_PCM_TEXT || 'From only €{pcm} PCM*';
  },
  // BF_PACKAGE_BADGE_1: первый бейдж
  get BF_PACKAGE_BADGE_1() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfPackageBadge1 || runtime.VITE_BF_PACKAGE_BADGE_1 || import.meta.env.VITE_BF_PACKAGE_BADGE_1 || 'ZERO DOWN OPTION**';
  },
  // BF_PACKAGE_BADGE_2: второй бейдж
  get BF_PACKAGE_BADGE_2() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfPackageBadge2 || runtime.VITE_BF_PACKAGE_BADGE_2 || import.meta.env.VITE_BF_PACKAGE_BADGE_2 || 'BUY-BACK GUARANTEE';
  },
  // BF_PACKAGE_BADGE_3: третий бейдж
  get BF_PACKAGE_BADGE_3() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfPackageBadge3 || runtime.VITE_BF_PACKAGE_BADGE_3 || import.meta.env.VITE_BF_PACKAGE_BADGE_3 || 'LOW CREDIT SCORE ACCEPTED';
  },
  // BF_PACKAGE_DISCLAIMER: дисклеймер под бейджами
  get BF_PACKAGE_DISCLAIMER() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfPackageDisclaimer || runtime.VITE_BF_PACKAGE_DISCLAIMER || import.meta.env.VITE_BF_PACKAGE_DISCLAIMER || '* Finance based on DUTY FREE price for personal loan through Eurobank';
  },
  // BF_LOAN_TERM_MONTHS: количество ежемесячных платежей (installmentMonths) для balloon loan
  get BF_LOAN_TERM_MONTHS() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_BF_LOAN_TERM_MONTHS || import.meta.env.VITE_BF_LOAN_TERM_MONTHS;
    return value ? parseInt(value, 10) : 23;
  },
  // BF_ANNUAL_RATE: годовая процентная ставка для balloon loan (0.1088 = 10.88%)
  get BF_ANNUAL_RATE() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_BF_ANNUAL_RATE || import.meta.env.VITE_BF_ANNUAL_RATE;
    return value ? parseFloat(value) : 0.1088;
  },
  // BF_BALLOON_PERCENT: доля lump sum от цены (0.50 = 50%)
  get BF_BALLOON_PERCENT() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_BF_BALLOON_PERCENT || import.meta.env.VITE_BF_BALLOON_PERCENT;
    return value ? parseFloat(value) : 0.50;
  },
  // BF_BALLOON_MONTH: месяц выплаты lump sum (24 = на 24-й месяц)
  get BF_BALLOON_MONTH() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_BF_BALLOON_MONTH || import.meta.env.VITE_BF_BALLOON_MONTH;
    return value ? parseInt(value, 10) : 24;
  },
  // BF_MAX_FINANCING: потолок суммы финансирования (€12,000). Всё сверху клиент
  // вносит deposit'ом → minDeposit = price - maxFinancing.
  get BF_MAX_FINANCING() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.bfMaxFinancing ?? runtime.VITE_BF_MAX_FINANCING ?? import.meta.env.VITE_BF_MAX_FINANCING;
    return value ? parseInt(value, 10) : 12000;
  },
  // BF_PACKAGE_CTA_TEXT: текст CTA-кнопки в BF Package card
  get BF_PACKAGE_CTA_TEXT() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfPackageCtaText || runtime.VITE_BF_PACKAGE_CTA_TEXT || import.meta.env.VITE_BF_PACKAGE_CTA_TEXT || 'Request an offer';
  },
  // BF_BADGE_COLOR: цвет бейджей (border + text). null → CarDetailPage фоллбэчит на var(--theme-primary)
  get BF_BADGE_COLOR() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfBadgeColor || runtime.VITE_BF_BADGE_COLOR || import.meta.env.VITE_BF_BADGE_COLOR || null;
  },
  // BF_SLIDER_COLOR: цвет thumb'а down-payment слайдера. null → CarDetailPage фоллбэчит на #111827
  get BF_SLIDER_COLOR() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.bfSliderColor || runtime.VITE_BF_SLIDER_COLOR || import.meta.env.VITE_BF_SLIDER_COLOR || null;
  },
  // BF_FINANCE_AI_CONSULTATION_ENABLED: показывать ли AI Consultation CTA в success-state формы Finance.
  // Greeting текст НЕ хранится тут — он в b2b_agent default_phrases.py, выбирается по launch_context.action='ask_about_finance'.
  get BF_FINANCE_AI_CONSULTATION_ENABLED() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.bfFinanceAiConsultationEnabled ?? runtime.VITE_BF_FINANCE_AI_CONSULTATION_ENABLED ?? import.meta.env.VITE_BF_FINANCE_AI_CONSULTATION_ENABLED;
    if (value === true || value === 'true') return true;
    return false;
  },
  // DUTY_FREE_GOV_FEE_TEXT: текст про правительственный сбор (например, "* Government fixed fee 150€")
  get DUTY_FREE_GOV_FEE_TEXT() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_DUTY_FREE_GOV_FEE_TEXT || import.meta.env.VITE_DUTY_FREE_GOV_FEE_TEXT || '* Government fixed fee 150€';
  },

  // Why buy from us block configuration
  // WHY_BUY_TITLE: заголовок блока
  get WHY_BUY_TITLE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whyBuyTitle || runtime.VITE_WHY_BUY_TITLE || import.meta.env.VITE_WHY_BUY_TITLE || 'Why buy from us';
  },
  // WHY_BUY_ITEM_1: первый пункт списка
  get WHY_BUY_ITEM_1() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whyBuyItem1 || runtime.VITE_WHY_BUY_ITEM_1 || import.meta.env.VITE_WHY_BUY_ITEM_1 || 'One-Stop-Shop (all under one roof)';
  },
  // WHY_BUY_ITEM_2: второй пункт списка
  get WHY_BUY_ITEM_2() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whyBuyItem2 || runtime.VITE_WHY_BUY_ITEM_2 || import.meta.env.VITE_WHY_BUY_ITEM_2 || 'Established in 1989';
  },
  // WHY_BUY_ITEM_3: третий пункт списка
  get WHY_BUY_ITEM_3() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whyBuyItem3 || runtime.VITE_WHY_BUY_ITEM_3 || import.meta.env.VITE_WHY_BUY_ITEM_3 || 'Up to 3 year warranty';
  },
  // WHY_BUY_ITEM_4: четвёртый пункт списка
  get WHY_BUY_ITEM_4() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whyBuyItem4 || runtime.VITE_WHY_BUY_ITEM_4 || import.meta.env.VITE_WHY_BUY_ITEM_4 || 'Tailor made packages that fit your needs';
  },
  // WHY_BUY_ITEM_5: пятый пункт списка
  get WHY_BUY_ITEM_5() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whyBuyItem5 || runtime.VITE_WHY_BUY_ITEM_5 || import.meta.env.VITE_WHY_BUY_ITEM_5 || '24/7 customer support';
  },

  // Widget branding
  // WIDGET_TITLE: название виджета (отображается в заголовке)
  get WIDGET_TITLE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.widgetTitle || runtime.VITE_WIDGET_TITLE || import.meta.env.VITE_WIDGET_TITLE || 'Ask AI';
  },

  // Задано ли название виджета явно (дилер, env). Нет — витрина на своём
  // стоке подписывает плашку на языке покупателя (#1431), дилерам
  // остаётся прежний 'Ask AI'.
  get WIDGET_TITLE_EXPLICIT() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return Boolean(mount.widgetTitle || runtime.VITE_WIDGET_TITLE || import.meta.env.VITE_WIDGET_TITLE);
  },

  // WhatsApp integration
  // SHOW_WHATSAPP: показывать кнопку WhatsApp в collapsed mode
  get SHOW_WHATSAPP() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.showWhatsApp || runtime.VITE_SHOW_WHATSAPP || import.meta.env.VITE_SHOW_WHATSAPP;
    return value === 'true' || value === true;
  },
  // WHATSAPP_NUMBER: номер WhatsApp (без +, только цифры, например: 31612345678)
  get WHATSAPP_NUMBER() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whatsAppNumber || runtime.VITE_WHATSAPP_NUMBER || import.meta.env.VITE_WHATSAPP_NUMBER || '';
  },
  // WHATSAPP_MESSAGE: предзаполненное сообщение для WhatsApp
  get WHATSAPP_MESSAGE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.whatsAppMessage || runtime.VITE_WHATSAPP_MESSAGE || import.meta.env.VITE_WHATSAPP_MESSAGE || '';
  },

  // Telegram integration (#1493)
  // SHOW_TELEGRAM: показывать кнопку Telegram в collapsed mode
  get SHOW_TELEGRAM() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.showTelegram || runtime.VITE_SHOW_TELEGRAM || import.meta.env.VITE_SHOW_TELEGRAM;
    return value === 'true' || value === true;
  },
  // TELEGRAM_HANDLE: хендл бота/канала (`name`, `@name` или `t.me/name`)
  get TELEGRAM_HANDLE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.telegramHandle || runtime.VITE_TELEGRAM_HANDLE || import.meta.env.VITE_TELEGRAM_HANDLE || '';
  },

  // CTA Buttons configuration
  // SHOW_CALL_BUTTON: показывать кнопку звонка (на мобилке)
  get SHOW_CALL_BUTTON() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_SHOW_CALL_BUTTON || import.meta.env.VITE_SHOW_CALL_BUTTON;
    // Default to true
    if (value === undefined || value === null || value === '') return true;
    return value === 'true' || value === true;
  },
  // SHOW_TALK_AI_BUTTON: показывать кнопку Talk AI (на десктопе)
  get SHOW_TALK_AI_BUTTON() {
    const runtime = getRuntimeConfig();
    const value = runtime.VITE_SHOW_TALK_AI_BUTTON ?? import.meta.env.VITE_SHOW_TALK_AI_BUTTON;
    // Default to true - кнопка показывается по умолчанию
    if (value === undefined || value === null) return true;
    return value === 'true' || value === true;
  },
  // CALL_BUTTON_TEXT: текст кнопки звонка
  get CALL_BUTTON_TEXT() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_CALL_BUTTON_TEXT || import.meta.env.VITE_CALL_BUTTON_TEXT || 'Call Now';
  },
  // TALK_AI_BUTTON_TEXT: текст кнопки Talk AI
  get TALK_AI_BUTTON_TEXT() {
    const runtime = getRuntimeConfig();
    return runtime.VITE_TALK_AI_BUTTON_TEXT || import.meta.env.VITE_TALK_AI_BUTTON_TEXT || 'Ask a Question';
  },
  // ENABLE_SERVICE_CENTER: включить модуль Service Center (ServiceProvider + routes)
  // По умолчанию выключен — ServiceContext вызывает fetchServiceCatalog() при mount,
  // что приводит к 404 если backend endpoint не существует
  // Runtime flag — управляется с бэкенда через PublicWidgetConfigResponseDto.features.enableServiceCenter
  // SHOW_AVAILABILITY_STATUS: показывать свободный текст наличия из инвентаря
  // (`car.availability`, например «Disponible en concession») бейджем на
  // карточке авто (#1330). По умолчанию ВЫКЛЮЧЕН.
  //
  // Структурную дату поставки (`car.deliveryDate` → «Arriving in March»)
  // карточка показывает всегда и без флага — это поведение с #1219, его флаг
  // не трогает. Флаг включает ВТОРОЙ источник: появление бейджа там, где его
  // раньше не было, меняет вид клиентской карточки.
  get SHOW_AVAILABILITY_STATUS() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value =
      mount.showAvailabilityStatus ??
      runtime.VITE_SHOW_AVAILABILITY_STATUS ??
      import.meta.env.VITE_SHOW_AVAILABILITY_STATUS;
    return value === 'true' || value === true;
  },

  // SHOW_EFFICIENCY_DISCLOSURE: показывать блок «расход / CO₂ / запас хода» на
  // карточке авто (#1330, #1331). По умолчанию ВЫКЛЮЧЕН.
  //
  // Почему выключен: данные приходят из инвентаря и есть у части дилеров, но
  // блок добавляет строки в карточку — то есть меняет вид клиентской
  // поверхности. Дилер получает его, когда сам включит, а не потому что мы
  // выкатили релиз. На витрине demo.shiftgears.ai включён через
  // VITE_SHOW_EFFICIENCY_DISCLOSURE в values.*.yaml.
  //
  // В Европе расход и выбросы объявляются вместе и с подписями — это то, что
  // покупатель ищет глазами (Pkw-EnVKV в Германии и аналоги).
  get SHOW_EFFICIENCY_DISCLOSURE() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value =
      mount.showEfficiencyDisclosure ??
      runtime.VITE_SHOW_EFFICIENCY_DISCLOSURE ??
      import.meta.env.VITE_SHOW_EFFICIENCY_DISCLOSURE;
    return value === 'true' || value === true;
  },

  // DEMO_PANEL_URL: адрес демо-панели b2b-ui на фикстурах (#1339) — путь этой
  // же витрины, `/panel/` (nginx проксирует его на релиз b2b-ui-demo). Пусто
  // по умолчанию — и тогда ни кнопки кабинета в шапке, ни ссылок на панель:
  // пока релиз не выкачен, они вели бы в 502. Включается строкой
  // VITE_DEMO_PANEL_URL в runtimeConfig values витрины. Только standalone-
  // витрина: в виджете на дилерском сайте вести посетителя в нашу панель незачем.
  get DEMO_PANEL_URL() {
    const runtime = getRuntimeConfig();
    return (
      runtime.VITE_DEMO_PANEL_URL || import.meta.env.VITE_DEMO_PANEL_URL || ''
    );
  },

  /**
   * Живой чат на странице «Чат с нами»: куда ведут кнопки под окном.
   * Telegram по умолчанию — бот клиента Andy's (своего демо-бота нет);
   * WhatsApp без адреса не показывается.
   */
  get LIVE_DEMO_TELEGRAM_URL() {
    const runtime = getRuntimeConfig();
    return (
      runtime.VITE_LIVE_DEMO_TELEGRAM_URL ||
      import.meta.env.VITE_LIVE_DEMO_TELEGRAM_URL ||
      'https://t.me/andys_motors_bot'
    );
  },

  /** Чей это бот — имя в заголовке блока; меняется вместе со ссылкой. */
  get LIVE_DEMO_CLIENT_NAME() {
    const runtime = getRuntimeConfig();
    return (
      runtime.VITE_LIVE_DEMO_CLIENT_NAME ||
      import.meta.env.VITE_LIVE_DEMO_CLIENT_NAME ||
      "Andy's Motors"
    );
  },

  get LIVE_DEMO_WHATSAPP_URL() {
    const runtime = getRuntimeConfig();
    return (
      runtime.VITE_LIVE_DEMO_WHATSAPP_URL ||
      import.meta.env.VITE_LIVE_DEMO_WHATSAPP_URL ||
      ''
    );
  },

  get ENABLE_SERVICE_CENTER() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.enableServiceCenter ?? runtime.VITE_ENABLE_SERVICE_CENTER ?? import.meta.env.VITE_ENABLE_SERVICE_CENTER;
    return value === 'true' || value === true;
  },
  // ENABLE_SHOW_WIDGET: advertise and execute the agent's `show_widget` command
  // (form / youtube / cards bubbles inside the chat). OFF by default (#1411):
  // no prompt or backend emits it, the chat does not render widget bubbles at
  // all (MessagesArea shows message text only — #1329), and the model used it
  // to improvise types like `car_details` / `rental-manage-booking`, then told
  // the customer the car was on screen while nothing appeared. Enable with
  // `enableShowWidget: true` on the mount (or VITE_ENABLE_SHOW_WIDGET) once a
  // flow actually needs it.
  get ENABLE_SHOW_WIDGET() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value =
      mount.enableShowWidget ??
      runtime.VITE_ENABLE_SHOW_WIDGET ??
      import.meta.env.VITE_ENABLE_SHOW_WIDGET;
    return value === 'true' || value === true;
  },
  // ENABLE_SHOWROOM_CATALOG: включить showroom-flow (issue #965) — каталог
  // на /showroom + Tinder-style swipe между mini-landing'ами + close × →
  // /showroom. По умолчанию выключен: пресет нужен только под Autodis-демо,
  // у Andys/Shift Gears остаётся pre-#965 landing (рендерится один
  // LandingBody без свайпа, без peek, без FAB).
  //
  // Когда true:
  //   - Route /showroom активен (ShowroomPage с useDealerPreset('autodis'))
  //   - CarLandingPage оборачивает LandingBody в swipe shell с peek/exit/FAB
  //   - CarCard в showroom-контексте навигирует на /landing
  //
  // Когда false:
  //   - /showroom не зарегистрирован (404)
  //   - CarLandingPage = простой fullscreen LandingBody (pre-#965)
  //
  // Runtime flag — может приехать с бэкенда через features.enableShowroomCatalog
  // или из VITE_ENABLE_SHOWROOM_CATALOG env var.
  // SHOWROOM_BRAND: чей это салон на /showroom и в свайп-лендинге (#1441).
  //   'autodis'    — исходный поток #965: пресет Autodis (тёмный), лого Autodis.
  //   'shiftgears' — витрина demo.shiftgears.ai на своём стоке: светлая тема
  //                  витрины, наш логотип.
  // Дилер по токену (?token в standalone или frontendToken в виджете) — всегда
  // его собственный салон: helm-значение витрины к нему не применяется, иначе
  // Autodis, открытый с demo.shiftgears.ai, перекрасился бы в нашу тему.
  get SHOWROOM_BRAND() {
    const mount = getMountParams();
    if (mount.showroomBrand) return mount.showroomBrand;
    if (!this.IS_OWN_STOCK) return 'autodis';
    const runtime = getRuntimeConfig();
    return runtime.VITE_SHOWROOM_BRAND || import.meta.env.VITE_SHOWROOM_BRAND || 'autodis';
  },

  // IS_OWN_STOCK: страница показывает СОБСТВЕННЫЙ сток витрины, а не дилера
  // (#1441). На demo.shiftgears.ai открываются и дилерские страницы — старый
  // формат QR `/#/cars/:id/landing?token=…` для дилеров без своего домена
  // (Andys, Autodis), — и в виджете у дилера есть frontendToken. Там ничего из
  // оформления витрины (перевод значений, радиусы, бренд салона) быть не должно.
  get IS_OWN_STOCK() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    if (getUrlParam('token') || mount.frontendToken) return false;
    return (runtime.VITE_ENVIRONMENT_NAME || import.meta.env.VITE_ENVIRONMENT_NAME) !== 'widget';
  },

  get ENABLE_SHOWROOM_CATALOG() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    // Дилер по токену — только его widget-config (#1441, ревью !1301). Флаг из
    // helm включён для своей витрины; если резолв токена не успел, он
    // протекал бы дилеру, и лендинг Andys открывался в оболочке шоурума.
    if (getUrlParam('token') || mount.frontendToken) {
      return mount.enableShowroomCatalog === true || mount.enableShowroomCatalog === 'true';
    }
    const value = mount.enableShowroomCatalog
      ?? runtime.VITE_ENABLE_SHOWROOM_CATALOG
      ?? import.meta.env.VITE_ENABLE_SHOWROOM_CATALOG;
    return value === 'true' || value === true;
  },

  // VOICE_ENABLED: голосовой режим виджета (#1353).
  //
  // Читается из НЕГАТИВНОГО флага `features.disableVoiceMode` — так дефолт
  // fail-safe: пока дилер явно не выключил голос, он есть (текущее поведение
  // для Andys / Demo Shift Gears сохраняется). Позитивный `voiceEnabled`
  // здесь опасен: `features` — jsonb-мешок, обе формы b2b-ui перезаписывают
  // его целиком, и один `?? false` где-нибудь по пути выключил бы голос всем.
  //
  // Когда false, гейтятся ВСЕ точки входа в голос (см. VoiceWidgetConnected):
  // микрофон в collapsed-баре, voice/chat тумблер в шапке чата, программный
  // `B2BDemo.open('voice')` / `openChat({mode:'voice'})`, restore в voice
  // после навигации (`sg_restore_widget`, `?sg-restore=voice`).
  get VOICE_ENABLED() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.disableVoiceMode
      ?? runtime.VITE_DISABLE_VOICE_MODE
      ?? import.meta.env.VITE_DISABLE_VOICE_MODE;
    return !(value === 'true' || value === true);
  },

  // ENABLE_RENT_PREFILL: config-driven Wheelsystems prefill bridge (#1108).
  // Default false — bridge stays inert until a dealer enables it.
  get ENABLE_RENT_PREFILL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    const value = mount.enableRentPrefill
      ?? runtime.VITE_ENABLE_RENT_PREFILL
      ?? import.meta.env.VITE_ENABLE_RENT_PREFILL;
    return value === 'true' || value === true;
  },

  // Legal pages URLs
  // TERMS_URL: URL для Terms of Service (по умолчанию: https://app.shiftgears.ai/terms)
  get TERMS_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.termsUrl || runtime.VITE_TERMS_URL || import.meta.env.VITE_TERMS_URL || 'https://app.shiftgears.ai/terms';
  },
  // PRIVACY_URL: URL для Privacy Policy
  // Конкретный URL задаётся через mount params (например, Andys → andys-motors.com/privacy-policy)
  // Дефолт — app.shiftgears.ai/privacy-policy; страница пока показывает те же
  // T&C, отдельного текста Privacy Policy ещё нет (#1387)
  get PRIVACY_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.privacyUrl || runtime.VITE_PRIVACY_URL || import.meta.env.VITE_PRIVACY_URL || 'https://app.shiftgears.ai/privacy-policy';
  },
  // DEALER_PRIVACY_URL: privacy policy ДИЛЕРА (widget-config legalPages.privacyUrl)
  // для ссылки в согласии на контакт лид-форм (#1440). В отличие от PRIVACY_URL
  // без env- и shiftgears-дефолта: не настроено → null → ссылку не показываем
  // (согласие на контакт от дилера не должно вести на нашу политику).
  // CONSENT_DEALER_NAME: имя дилера для `{dealer}` в тексте согласия (#1440).
  // Только из API widget-config (mount.consentDealerName, см. widgetConfigLoader):
  // b2b-api пишет формулировку с тем же полем. URL/iframe/script-атрибуты и env
  // сюда не попадают — иначе показанный и записанный тексты разойдутся.
  // null → «the dealer» в локали посетителя (так же, как на сервере).
  get CONSENT_DEALER_NAME() {
    const name = getMountParams().consentDealerName;
    return typeof name === 'string' && name.trim() ? name.trim() : null;
  },
  get DEALER_PRIVACY_URL() {
    const mount = getMountParams();
    const runtime = getRuntimeConfig();
    return mount.privacyUrl || runtime.VITE_PRIVACY_URL || null;
  },
};

// Log configuration in development (при первом обращении к config)
if (import.meta.env.DEV) {
  // Используем setTimeout чтобы дать время widget.jsx установить window.runtimeConfig
  setTimeout(() => {
    // Под vitest этот таймер нередко срабатывает уже ПОСЛЕ сноса jsdom-окружения
    // теста, который случайно импортировал модуль, — и `window is not defined`
    // валит весь ран в exit 1 при 625 зелёных тестах. Лог диагностический,
    // без window ему нечего сказать.
    if (typeof window === 'undefined') return;
    console.log('[RuntimeConfig] Using configuration:', {
      source: window.runtimeConfig ? 'window.runtimeConfig' : 'import.meta.env',
      config: {
        CAR_API_URL: config.CAR_API_URL,
        ORGANIZATION_ID: config.ORGANIZATION_ID,
        INVENTORY_SLUG: config.INVENTORY_SLUG,
      }
    });
  }, 100);
}
